import { css } from 'aphrodite';
import * as React from 'react';
import { IPlatformValidationError } from '../../../../models/utils/socialPostValidation';
import { Button } from '../../../components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogClose,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import { THEME } from '../../../components-v2/Theme/theme';
import { DangerIcon } from '../../svgs/icons/DangerIcon';
import { styleSheet } from './styles';

export interface ISchedulePreflightDialogProps {
	/** What the user was doing, which the dialog names. A draft save answers to the save rules alone. */
	action?: 'save' | 'schedule';
	/** Everything the API would refuse. The dialog is shown only while this is non-empty. */
	errors: IPlatformValidationError[];
	isOpen: boolean;
	onRequestClose(): void;
}

const POST_WIDE_HEADING = 'This post';

const groupByPlatform = (errors: IPlatformValidationError[]) => {
	const groups = new Map<string, IPlatformValidationError[]>();
	errors.forEach(error => {
		const heading = error.platform ?? POST_WIDE_HEADING;
		const existing = groups.get(heading);
		if (existing) {
			existing.push(error);
			return;
		}
		groups.set(heading, [error]);
	});
	return [...groups.entries()];
};

const Row: React.FC<{ error: IPlatformValidationError }> = ({ error }) => (
	<li className={css(styleSheet.row)}>
		<span className={css(styleSheet.rowIconSlot)}>
			<DangerIcon
				backgroundColor={THEME.colors.danger.main}
				borderColor={THEME.colors.danger.light}
				className={css(styleSheet.rowIcon)}
			/>
		</span>
		<span>{error.message}</span>
	</li>
);

/**
 * The last thing between a post and the API. The composer already disables the schedule controls
 * while a version is over a limit, but the confirm sits a route away from the editor and the draft
 * can change under it — so the same rules run again here, and every reason is named rather than
 * summarised.
 *
 * There is no "schedule anyway": everything listed is something the API refuses, so proceeding would
 * only move the refusal.
 */
export const SchedulePreflightDialog: React.FC<ISchedulePreflightDialogProps> = ({
	action = 'schedule',
	errors,
	isOpen,
	onRequestClose,
}) => {
	const onOpenChange = (open: boolean) => {
		if (!open) {
			onRequestClose();
		}
	};

	const grouped = groupByPlatform(errors);
	const verb = action === 'save' ? 'saved' : 'scheduled';
	const retry = action === 'save' ? 'save' : 'schedule';

	return (
		<Dialog dismissOnOutsideClick={false} onOpenChange={onOpenChange} open={isOpen}>
			<DialogPopup styles={[styleSheet.popup]}>
				<DialogHeader styles={[styleSheet.header]}>
					<DangerIcon
						backgroundColor={THEME.colors.danger.main}
						borderColor={THEME.colors.danger.light}
						className={css(styleSheet.headerIcon)}
					/>
					<DialogTitle styles={[styleSheet.title]}>This post can&apos;t be {verb} yet</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<p className={css(styleSheet.description)}>
						{grouped.length === 1
							? `Fix the following, then ${retry} again.`
							: `Fix the following on each platform, then ${retry} again.`}
					</p>
					{grouped.map(([heading, groupErrors]) => (
						<section className={css(styleSheet.group)} key={heading}>
							<h4 className={css(styleSheet.groupHeading)}>{heading}</h4>
							<ul className={css(styleSheet.rows)}>
								{groupErrors.map((error, index) => (
									<Row error={error} key={`${error.field}-${index}`} />
								))}
							</ul>
						</section>
					))}
				</DialogBody>
				<DialogFooter>
					<DialogClose render={<Button variant='primary'>Back to post</Button>} />
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
